/**
 * ScaleAds Copilot — conversational ad operations.
 *
 * Two things this UI must get right:
 *
 *  1. Show the work. Every tool call Copilot makes is rendered inline, so the
 *     merchant can see *why* an action was taken, not just that it happened.
 *     An AI that moves money without a visible audit trail is not trustworthy.
 *
 *  2. Make approvals unmissable. Anything above $100/day spend-at-risk comes
 *     back as a pending approval — those render as blocking cards, not toasts.
 */
import { useEffect, useRef, useState } from 'react';
import { api, fmt, ApiError, type CopilotReply } from '../api';

type Message =
  | { role: 'user'; text: string }
  | {
      role: 'assistant';
      text: string;
      toolCalls: CopilotReply['toolCalls'];
      approvals: CopilotReply['pendingApprovals'];
    }
  | { role: 'error'; text: string };

const SUGGESTIONS = [
  'Get my CPA under $8 this week',
  'Show me my worst-performing creative right now',
  'Create 3 paused Meta variants of my best hook with a $30/day limit',
  'Why did Meta spend jump yesterday?',
  'What competitor angles am I missing?',
];

export default function Copilot() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [resolved, setResolved] = useState<Record<string, 'approved' | 'rejected'>>({});
  const threadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  async function send(text: string) {
    if (!text.trim() || busy) return;
    setMessages((m) => [...m, { role: 'user', text }]);
    setInput('');
    setBusy(true);

    try {
      const reply = await api.copilotChat(text, conversationId);
      setConversationId(reply.conversationId);
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          text: reply.reply,
          toolCalls: reply.toolCalls,
          approvals: reply.pendingApprovals,
        },
      ]);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.status === 429
            ? 'Rate limited — Copilot is capped at 20 messages a minute. Give it a moment.'
            : `${err.message}${err.requestId ? ` (request ${err.requestId})` : ''}`
          : 'Something went wrong reaching Copilot.';
      setMessages((m) => [...m, { role: 'error', text: msg }]);
    } finally {
      setBusy(false);
    }
  }

  async function decide(id: string, approve: boolean) {
    setResolved((r) => ({ ...r, [id]: approve ? 'approved' : 'rejected' }));
    try {
      if (approve) await api.approveCopilot(id);
      else await api.rejectCopilot(id);
    } catch {
      setResolved((r) => {
        const next = { ...r };
        delete next[id];
        return next;
      });
    }
  }

  return (
    <>
      <p className="eyebrow">Conversational ad ops</p>
      <h1 className="pg-title">ScaleAds Copilot</h1>
      <p className="pg-sub">
        Describe an outcome. Copilot reads your accounts, decides what to change, and asks before
        risking more than $100/day. Every action is reversible for 24 hours.
      </p>

      <div className="grid-2to1">
        <div className="card copilot-shell">
          <div className="copilot-thread" ref={threadRef}>
            {messages.length === 0 && (
              <div className="copilot-empty">
                <div className="copilot-avatar assistant">C</div>
                <div>
                  <p style={{ marginBottom: 12 }}>
                    I can read your Meta campaigns and prepare approved changes. Try one of these:
                  </p>
                  {SUGGESTIONS.map((s) => (
                    <button key={s} className="suggestion" onClick={() => void send(s)}>
                      “{s}”
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <MessageBubble
                key={i}
                message={m}
                resolved={resolved}
                onDecide={decide}
              />
            ))}

            {busy && (
              <div className="copilot-msg">
                <div className="copilot-avatar assistant">C</div>
                <div className="copilot-bubble">
                  <span className="typing">Reading your account state…</span>
                </div>
              </div>
            )}
          </div>

          <form
            className="copilot-input"
            onSubmit={(e) => {
              e.preventDefault();
              void send(input);
            }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Describe an outcome, or ask a question…"
              disabled={busy}
              aria-label="Message Copilot"
            />
            <button type="submit" className="btn brand" disabled={busy || !input.trim()}>
              Send
            </button>
          </form>
        </div>

        <Sidebar />
      </div>
    </>
  );
}

function MessageBubble({
  message,
  resolved,
  onDecide,
}: {
  message: Message;
  resolved: Record<string, 'approved' | 'rejected'>;
  onDecide: (id: string, approve: boolean) => void;
}) {
  if (message.role === 'user') {
    return (
      <div className="copilot-msg user">
        <div className="copilot-avatar user">You</div>
        <div className="copilot-bubble user">{message.text}</div>
      </div>
    );
  }

  if (message.role === 'error') {
    return (
      <div className="copilot-msg">
        <div className="copilot-avatar error">!</div>
        <div className="copilot-bubble error">{message.text}</div>
      </div>
    );
  }

  return (
    <div className="copilot-msg">
      <div className="copilot-avatar assistant">C</div>
      <div className="copilot-bubble">
        <div className="copilot-text">{message.text}</div>

        {message.toolCalls.length > 0 && <ToolTrace calls={message.toolCalls} />}

        {message.approvals.map((a) => (
          <div key={a.id} className={`approval ${resolved[a.id] ?? ''}`}>
            <h6>
              {a.kind.replace(/_/g, ' ')} · {a.target}
            </h6>
            <p>
              {a.reason} · spend at risk:{' '}
              <strong>{fmt.money(a.spend_at_risk_cents)}/day</strong>
            </p>
            {resolved[a.id] ? (
              <span className={`badge ${resolved[a.id] === 'approved' ? 'b-active' : 'b-paused'}`}>
                {resolved[a.id]}
              </span>
            ) : (
              <div className="row gap-2">
                <button className="btn brand sm" onClick={() => onDecide(a.id, true)}>
                  Approve
                </button>
                <button className="btn sm" onClick={() => onDecide(a.id, false)}>
                  Dismiss
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** The audit trail. Collapsed by default so it informs without shouting. */
function ToolTrace({ calls }: { calls: CopilotReply['toolCalls'] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="tool-trace">
      <button className="tool-toggle" onClick={() => setOpen((o) => !o)}>
        {open ? '▾' : '▸'} {calls.length} tool {calls.length === 1 ? 'call' : 'calls'}
      </button>
      {open && (
        <div className="tool-list">
          {calls.map((c, i) => (
            <div key={i} className="tool-call">
              <span className="tool-name">{c.name}</span>
              <span className="tool-args">({summarise(c.input)})</span>
              <div className="tool-result">← {summarise(c.output)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function summarise(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v.length > 80 ? `${v.slice(0, 80)}…` : v;
  const s = JSON.stringify(v);
  return s.length > 120 ? `${s.slice(0, 120)}…` : s;
}

function Sidebar() {
  const [digest, setDigest] = useState<Awaited<ReturnType<typeof api.operatorDigest>> | null>(null);

  useEffect(() => {
    api.operatorDigest().then(setDigest).catch(() => undefined);
  }, []);

  return (
    <div className="stack">
      <div className="card card-p">
        <h4>Guardrails</h4>
        <table className="data guardrails">
          <tbody>
            <tr>
              <td>Budget delta cap</td>
              <td className="num">±30%</td>
            </tr>
            <tr>
              <td>Auto-apply threshold</td>
              <td className="num">$100/day</td>
            </tr>
            <tr>
              <td>Revert window</td>
              <td className="num">24h</td>
            </tr>
            <tr>
              <td>Audit log</td>
              <td className="num ok">every call</td>
            </tr>
            <tr>
              <td>Destructive ops</td>
              <td className="num err">blocked</td>
            </tr>
          </tbody>
        </table>
      </div>

      {digest && (
        <div className="card card-p">
          <h4>
            This week <span className="meta">AI Operator</span>
          </h4>
          <p className="digest">{digest.summary}</p>
          {digest.actions.pending > 0 && (
            <p className="warn-line">{digest.actions.pending} awaiting your approval</p>
          )}
        </div>
      )}

      <div className="card card-p">
        <h4>What Copilot can reach</h4>
        <ul className="tool-inventory">
          <li>Meta Ads — read, pause, budget, launch</li>
          <li>Google Ads — roadmap, not enabled for campaign writes</li>
          <li>Your hook library + winners</li>
          <li>Competitor ad intelligence</li>
          <li>Accepted UGC videos</li>
          <li>True (incremental) ROAS</li>
        </ul>
        <p className="footnote">
          No Reddit posting capability exists — paid ads only, by design.
        </p>
      </div>
    </div>
  );
}
